import { useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import YoutubeUrlInputField from "./YoutubeUrlInputField";
import ContainerOverlay from "../../../Containers/ContainerOverlay";
import YoutubeEmbedPreview, {
  YOUTUBE_EMBED_PREVIEW_BORDER_RADIUS,
} from "../../../Media/YoutubeEmbedPreview";
import { getYoutubeVideoIdFromUrl } from "../../../../utils/string";
import { getReactHookFormNestedError } from "../../../../utils/errors";

const getVideoUrlFieldError = ({ errors, name, index }) =>
  getReactHookFormNestedError(errors, `${name}.[${index}]`);

const ContainerVideosYoutube = ({
  name,
  disabled = false,
  maxVideos = 5,
  showUrlInput = false,
  showTooltip = false,
  titleTooltip = "",
  actionTooltip = "",
}) => {
  const formContext = useFormContext();
  const watch = formContext?.watch;
  const setValue = formContext?.setValue;
  const clearErrors = formContext?.clearErrors;
  const errors = formContext?.formState?.errors || {};
  const theme = useTheme();

  const value = watch(name) || [];
  const videos = value.filter(Boolean);

  const onChange = (nextVideos) => {
    setValue(name, nextVideos, { shouldDirty: true, shouldValidate: true });
  };

  const onRemoveVideo = (originalIndex) => {
    clearErrors(`${name}.[${originalIndex}]`);
    const newVideos = value.filter((_, i) => i !== originalIndex);
    onChange(newVideos);
  };

  const onUrlAdded = ({ youtubeUrl }) => {
    onChange([...videos, youtubeUrl]);
  };

  const showInput = showUrlInput && !disabled;
  if (!showInput && videos.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: videos.length > 0 ? 2 : 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {showUrlInput && !disabled ? (
        <YoutubeUrlInputField
          currentUrls={videos}
          maxVideos={maxVideos}
          disabled={disabled}
          showTooltip={showTooltip}
          titleTooltip={titleTooltip}
          actionTooltip={actionTooltip}
          onCallbackAddUrl={onUrlAdded}
        />
      ) : null}

      {videos.length > 0 ? (
        <Grid container spacing={2}>
          {value.map((videoUrl, originalIndex) => {
            if (!videoUrl) return null;

            const youtubeVideoId = getYoutubeVideoIdFromUrl({ url: videoUrl });
            const fieldError = getVideoUrlFieldError({
              errors,
              name,
              index: originalIndex,
            });
            const errorMessage = fieldError?.message;
            const fieldPath = `${name}.[${originalIndex}]`;

            return (
              <Grid
                item
                xs={12}
                sm={6}
                key={`${videoUrl}-${originalIndex}`}
                id={fieldPath}
                data-field={fieldPath}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      borderRadius: YOUTUBE_EMBED_PREVIEW_BORDER_RADIUS,
                      overflow: "hidden",
                      border: errorMessage
                        ? `1px solid ${theme.palette.error.main}`
                        : "none",
                      "&:hover .overlay": {
                        opacity: 1,
                      },
                    }}
                  >
                    <YoutubeEmbedPreview
                      youtubeVideoId={youtubeVideoId}
                      sx={{ width: "100%" }}
                    />
                    {!disabled ? (
                      <ContainerOverlay
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          top: 0,
                          right: 0,
                          left: 0,
                          borderTopLeftRadius:
                            YOUTUBE_EMBED_PREVIEW_BORDER_RADIUS,
                          borderTopRightRadius:
                            YOUTUBE_EMBED_PREVIEW_BORDER_RADIUS,
                        }}
                      >
                        <IconButton
                          edge="start"
                          sx={{ p: 0 }}
                          onClick={() => onRemoveVideo(originalIndex)}
                        >
                          <DeleteIcon
                            sx={{
                              color: "white !important",
                            }}
                          />
                        </IconButton>
                      </ContainerOverlay>
                    ) : null}
                  </Box>
                  {errorMessage ? (
                    <Typography variant="caption" color="error">
                      {errorMessage}
                    </Typography>
                  ) : null}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      ) : null}
    </Box>
  );
};

export default ContainerVideosYoutube;
